import { AUDIO_REELS_FLAG } from "./constants";
import { FF, targetingListsSchema, VariantFF } from "./ff";
import { FFId, FFInput } from "./inputs";
import { dynamicIncentivePayloadSchema } from "@/components/interview/dynamicIncentive/shared";
import {
  type ExternalPanelKey,
  getCustomSrcValidationError,
  getExternalPanelByKey,
} from "@/components/outreach/external_panels/constants";
import { earlyQualityPayloadSchema } from "@/components/responses/evaluation/quality/early/trigger";
import { VENDORS } from "@/lib/vendor-policy/types";
import {
  isStreamableInterviewerModel,
  type STREAMABLE_MODEL,
} from "@/utils/constants";
import { isNullish, notNull } from "@/utils/helpers";
import { z } from "zod";

// Every flag read through featureFlagSdk.ts is declared here; PostHog targeting may only use the declared inputs.
// See docs/feature_flags.md.

// Global switch, off until admin-dashboard corrections ship. The flag key is the distinct id so every caller shares one bucket.
export const CreditAllocationCorrectionDirtyRange = new FF(
  "credit-allocation-correction-dirty-range",
  { id: FFId.anonymousId }
);

// Customer Slack app: entry points (Settings card, Slack Home tab connect) per workspace. The Slack routes are not gated.
export const CustomerSlack = new FF(
  "customer-slack",
  { id: FFId.orgId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

export const StudySummaryVideo = new FF("study-summary-video", {
  id: FFId.userId,
  email: FFInput.email,
});

// Creation chat: the "flag this reply" button. Super-admins only.
export const CreationChatRating = new FF(
  "creation-chat-rating",
  { id: FFId.userId, isSuperAdmin: FFInput.isSuperAdmin },
  { isAlwaysOnForSuperAdmins: true }
);

// Respondent entry: a study link marked closed blocks new entries.
export const StudyLinkStatusGating = new FF("study-link-status-gating", {
  id: FFId.surveyId,
  surveyId: FFInput.surveyId,
});

export const ProlificLaunchTargetMultiplier = new FF(
  "prolific-launch-target-multiplier",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

export const UserInterviewsLaunchTargetMultiplier = new FF(
  "user-interviews-launch-target-multiplier",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Signup: global kill switch for new signups.
export const AllowSignups = new FF(
  "allow_signups",
  { id: FFId.global },
  { payload: z.object({ enabled: z.boolean() }) }
);

// Interview: media and per-study respondent options.
export const SignedMedia = new FF(
  "signed-media",
  { id: FFId.orgId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

export const HealthPolicyCheckbox = new FF(
  "health-policy-checkbox",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

export const DisableLLMInInterview = new FF(
  "disableLLMInInterview",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Sharing and exports.
export const ForceDisableLinkSharing = new FF(
  "force-disable-link-sharing",
  { id: FFId.orgId, orgId: FFInput.orgId, surveyId: FFInput.surveyId },
  { usesTargetingLists: true }
);

// Sharing: the restricted-study UI (create switch, Share dialog v3, badges). Enforcement is not gated.
export const EnableRestrictedStudies = new FF(
  "enable-restricted-studies",
  { id: FFId.orgId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

export const ExportExcludeFollowUps = new FF(
  "export-exclude-follow-ups",
  { id: FFId.orgId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Analysis generation.
export const ShowLegacyHighlights = new FF(
  "show-legacy-highlights",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Old per-question reels from the summary run; Question Analysis now makes its own.
export const LegacyQuestionClips = new FF(
  "legacy-question-clips",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

export const LegacyPresentationGen = new FF(
  "legacy-presentation-gen",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Gates Fable 5 per workspace. Off falls back to Opus, and the proxy is told
// (via the signed token) to use the default zero-retention key instead of the
// 30-day-retention key Fable requires, so privacy-sensitive workspaces can opt
// out of both the model and the retention.
export const FablePresentations = new FF(
  "fable-presentations",
  { id: FFId.orgId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Responses: screenouts, video quality, reels and redaction.
export const AllowContinuedScreenouts = new FF(
  "allow-continued-screenouts",
  { id: FFId.responseId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

export const HideBadQualityVideos = new FF(
  "hide-bad-quality-videos",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Reels: the de-identified version (blurred face, converted voice), for orgs in the payload's lists.
export const DeidentifiedReels = new FF(
  "deidentified-reels",
  { id: FFId.orgId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

export const ScreenRecordingForReels = new FF(
  "screen-recording-for-reels",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Lets audio-only studies make reels, with a sound card as each clip's picture.
export const AudioReels = new FF(
  AUDIO_REELS_FLAG,
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

export const MarkAllMessagesRedacted = new FF(
  "mark-all-messages-redacted",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Fraud: longitudinal profile checks.
export const ProfileConsistencyFraudOverride = new FF(
  "profile-consistency-fraud-override",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Identity: response cooldown.
export const EnableForLinkRecruitments = new FF(
  "enable-for-link-recruitments",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Recruitment: audience plans UI, prediction, and provisioning.
export const AudiencePlansRespondent = new FF(
  "audience-plans-respondent",
  {
    id: FFId.surveyId,
    surveyId: FFInput.surveyId,
    orgId: FFInput.orgId,
    userId: FFInput.userId,
  },
  { usesTargetingLists: true }
);

export const AudiencePlansUi = new FF(
  "audience-plans-ui",
  {
    id: FFId.userId,
    surveyId: FFInput.surveyId,
    orgId: FFInput.orgId,
  },
  { usesTargetingLists: true }
);

export const AudienceTgProvisioning = new FF(
  "audience-tg-provisioning",
  {
    id: FFId.surveyId,
    surveyId: FFInput.surveyId,
    orgId: FFInput.orgId,
    userId: FFInput.userId,
  },
  { usesTargetingLists: true }
);

export const AudiencePredictionWorkflow = new FF(
  "audience-prediction-workflow",
  {
    id: FFId.surveyId,
    surveyId: FFInput.surveyId,
    orgId: FFInput.orgId,
    userId: FFInput.userId,
  },
  { usesTargetingLists: true }
);

// Public API and email sending.
// Payload `orgs` lists the organizations allowed to call the video import routes and the MCP get_reel_download_url tool.
export const PublicApiVideoImport = new FF(
  "public-api-video-import",
  { id: FFId.orgId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

export const EmailSenderV2 = new FF(
  "EmailSenderV2",
  { id: FFId.orgId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Analysis agent runs. Study and org lists in the payload.
export const AnalysisChatUseClaude = new FF(
  "analysis-chat-use-claude",
  { id: FFId.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Analysis agent canvas: report generation and the report edit chat, keyed by study since report generation has no user.
export const CanvasAgentReport = new FF(
  "canvas-agent-report",
  { id: FFId.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Analysis agent canvas: analysis chat.
export const CanvasAgentChat = new FF(
  "canvas-agent-chat",
  {
    id: FFId.userId,
    email: FFInput.email,
    orgId: FFInput.orgId,
    surveyId: FFInput.surveyId,
  },
  { usesTargetingLists: true }
);

// Recruitment: orgs listed in the payload can't use the consumer panel.
export const DisableConsumerPanel = new FF(
  "disable-consumer-panel",
  { id: FFId.orgId, orgId: FFInput.orgId, surveyId: FFInput.surveyId },
  { usesTargetingLists: true }
);

// Creation: more than the default number of quotas per study.
export const ManyQuotas = new FF(
  "many-quotas",
  {
    id: FFId.userId,
    isSuperAdmin: FFInput.isSuperAdmin,
    orgId: FFInput.orgId,
    surveyId: FFInput.surveyId,
  },
  { usesTargetingLists: true, isAlwaysOnForSuperAdmins: true }
);

// Creation: the conjoint question type. The payload lists users, orgs and studies.
export const ConjointQuestion = new FF(
  "conjoint-question",
  { id: FFId.userId, orgId: FFInput.orgId, surveyId: FFInput.surveyId },
  { usesTargetingLists: true }
);

// Analysis: conjoint model fits. Keyed on the study, since fits run without a user.
export const ConjointFitWorkflow = new FF(
  "conjoint-fit-workflow",
  { id: FFId.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Creation: questions participants can skip. User and org lists in the payload.
export const OptionalQuestions = new FF(
  "optional-questions",
  { id: FFId.userId, email: FFInput.email, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Creation: consent form gates in screeners. User and org lists in the payload.
export const ScreenerConsentForm = new FF(
  "screener-consent-form",
  { id: FFId.userId, email: FFInput.email, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Creation: verification on upload questions. User, org and study lists in the payload.
export const UploadVerification = new FF(
  "upload-verification",
  {
    id: FFId.userId,
    email: FFInput.email,
    orgId: FFInput.orgId,
    surveyId: FFInput.surveyId,
  },
  { usesTargetingLists: true }
);

// Always on (Listen Pulse) studies while in pilot: the Details v2 wave picker and wave data.
export const AlwaysOn = new FF(
  "listen-pulse",
  {
    id: FFId.userId,
    isSuperAdmin: FFInput.isSuperAdmin,
    orgId: FFInput.orgId,
    surveyId: FFInput.surveyId,
  },
  { usesTargetingLists: true, isAlwaysOnForSuperAdmins: true }
);

// Creation: @mentions in study comments and the notifications they send.
export const CommentNotifications = new FF(
  "comment-notifications",
  { id: FFId.orgId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Recruitment: self-serve external panel dashboards.
export const ExternalDashboard = new FF(
  "external-dashboard",
  {
    id: FFId.userId,
    email: FFInput.email,
    isSuperAdmin: FFInput.isSuperAdmin,
    orgId: FFInput.orgId,
    surveyId: FFInput.surveyId,
  },
  { usesTargetingLists: true, isAlwaysOnForSuperAdmins: true }
);

// Recruitment: external dashboards with a custom panel or link.
export const ExternalDashboardCustom = new FF(
  "external-dashboard-custom",
  {
    id: FFId.userId,
    email: FFInput.email,
    isSuperAdmin: FFInput.isSuperAdmin,
    orgId: FFInput.orgId,
    surveyId: FFInput.surveyId,
  },
  { usesTargetingLists: true, isAlwaysOnForSuperAdmins: true }
);

// Recruitment: the outreach page and the legacy email sender.
export const OutreachPage = new FF(
  "outreach-page",
  {
    id: FFId.userId,
    email: FFInput.email,
    isSuperAdmin: FFInput.isSuperAdmin,
  },
  { isAlwaysOnForSuperAdmins: true }
);

// Recruitment: download screener answers from the outreach page.
export const OutreachDownloadScreeners = new FF("outreach-download-screeners", {
  id: FFId.userId,
  email: FFInput.email,
});

// Analysis: regenerate tags after an external data import.
export const RegenerateTagsForSurvey = new FF(
  "regenerate-tags-for-survey",
  { id: FFId.userId, isSuperAdmin: FFInput.isSuperAdmin },
  { isAlwaysOnForSuperAdmins: true }
);

// Interviewer: prompt version and model per participant.
const SUPPORTED_PROMPT_VERSIONS: readonly number[] = [12, 13, 14, 15, 16];

export const InterviewPromptVariant = new VariantFF(
  "interview-prompt-variant",
  { id: FFId.participantId },
  {
    payload: z.object({
      promptVersion: z
        .number()
        .refine((version) => SUPPORTED_PROMPT_VERSIONS.includes(version)),
      model: z.custom<STREAMABLE_MODEL>(isStreamableInterviewerModel),
    }),
  }
);

// Interviewer: Submit button icon A/B test per participant (on: arrow-right, off: stop).
export const SubmitArrowIcon = new FF("submit-arrow-icon", {
  id: FFId.participantId,
});

// End page: redirect end pages auto-continue for these panels, or for custom srcs no panel claims.
export const EndPageAutoContinue = new FF(
  "end-page-auto-continue",
  { id: FFId.global },
  {
    payload: z
      .object({
        panels: z.array(
          z.custom<ExternalPanelKey>(
            (value) =>
              typeof value === "string" && notNull(getExternalPanelByKey(value))
          )
        ),
        srcs: z
          .array(
            z.custom<string>(
              (value) =>
                typeof value === "string" &&
                isNullish(getCustomSrcValidationError(value))
            )
          )
          .optional(),
      })
      .strict(),
  }
);

// Response quality: early checks on in-progress interviews.
export const EarlyQualityCheck = new VariantFF(
  "early-quality-check",
  { id: FFId.responseId },
  { payload: earlyQualityPayloadSchema }
);

// Prolific: time-based bonus for early QC screen-outs (sends real money).
export const EarlyQcScreenOutBonus = new FF("early-qc-screenout-bonus", {
  id: FFId.responseId,
});

// Auth: signup and user-creation settings.
export const SignupDomainBlacklist = new FF(
  "signup-domain-blacklist",
  { id: FFId.global },
  { payload: z.object({ domains: z.array(z.string()) }) }
);

export const SkipBeforeUserCreateHookKeys = new FF(
  "skip-before-user-create-hook-keys",
  { id: FFId.global },
  { payload: z.object({ keys: z.array(z.string()) }) }
);

export const AugmentedListenSsoLogin = new FF(
  "augmented-listen-sso-login",
  { id: FFId.global },
  {
    // Strict: a misspelled key such as `excludedOrg` must fail closed, not be dropped.
    payload: targetingListsSchema
      .pick({ enabled: true, orgs: true, excludedOrgs: true })
      .strict(),
  }
);

// Compliance: org lists read by the 2026_08 compliance policy backfill script.
export const ZeroDataRetention = new FF(
  "zero-data-retention",
  { id: FFId.global },
  { payload: targetingListsSchema }
);

export const BannedProviders = new FF(
  "banned-providers",
  { id: FFId.global },
  {
    payload: targetingListsSchema.extend({
      bannedProviders: z.array(z.enum(VENDORS)),
    }),
  }
);

// Billing: credits granted to a self-serve org on onboarding.
export const DefaultCreditBalance = new FF(
  "default-credit-balance",
  { id: FFId.global },
  { payload: z.number() }
);

// Billing: wallet charging, credit holds and strict blocking for the org.
export const BillingPageV2 = new FF(
  "billing-page-v2",
  { id: FFId.orgId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Incentives: CNY amounts in the editor and CNY payouts. The payload lists both users and orgs.
export const CnyIncentives = new FF(
  "CNY-Incentives",
  { id: FFId.userId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Interview: bonus offered to a treatment arm of recruited Prolific participants.
export const DynamicIncentiveExperiment = new FF(
  "dynamic-incentive-experiment",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  {
    usesTargetingLists: true,
    payload: dynamicIncentivePayloadSchema,
  }
);

// Analysis chat: studies whose embedding queries are subsampled.
export const SubsampledAnalysisChat = new FF(
  "subsampled-analysis-chat",
  { id: FFId.global },
  {
    payload: z.object({
      studies: z.array(z.string()),
      sampling_limit: z.number().optional(),
    }),
  }
);

// Reels: Editframe as primary renderer, bucketed per reel owner so partial rollouts don't reshuffle.
export const EditframeRendering = new FF("editframe-rendering", {
  id: FFId.anonymousId,
});

// Responses: hides the participant source column from non-staff viewers, except in excluded orgs.
export const HideSrcColumn = new FF(
  "hide-src-column",
  { id: FFId.userId, email: FFInput.email, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Creation chat: per-language variants of embedded media. Staff and super-admins only.
export const PerLanguageEmbedVariants = new FF(
  "per-language-embed-variants",
  {
    id: FFId.userId,
    email: FFInput.email,
    isSuperAdmin: FFInput.isSuperAdmin,
  },
  { isAlwaysOnForSuperAdmins: true }
);

// Analysis: lazily render report sections. Study lists in the payload.
export const AnalysisLazyRendering = new FF(
  "analysis-lazy-rendering",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// One research library chat that follows the user across the app. It runs on the AgentExecutor and lives in analysis_chats_v2.
export const UnifiedChat = new FF(
  "unified-chat",
  { id: FFId.userId, email: FFInput.email, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Recruitment: the hourly job that refills Prolific recruits that lost completes after filling.
export const ProlificTopUp = new FF(
  "prolific-top-up",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

export const UserInterviewsTopUp = new FF(
  "user-interviews-top-up",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);

// Recruitment: the per-minute orchestrator that evaluates recruitment health for launched studies.
export const RecruitmentHealthMonitor = new FF("recruitment-health-monitor", {
  id: FFId.global,
});

// PostgREST retirement: shadow reads that compare PostgREST with its Drizzle replica, switched per query.
// `queries` lists the compared query names; `cutover` lists those whose callers get the replica's result.
export const PostgrestShadowReads = new FF(
  "postgrest-shadow-reads",
  { id: FFId.global },
  {
    payload: z.object({
      queries: z.array(z.string()),
      cutover: z.array(z.string()).optional(),
    }),
  }
);

// Recruitment: linkDataForProlificPanelWorkflow runs only when this flag is on.
// A failed flag read stays off, so the workflow does not start.
export const LinkDataForProlificPanelWorkflow = new FF(
  "link-data-for-prolific-panel-workflow",
  { id: FFId.surveyId, surveyId: FFInput.surveyId, orgId: FFInput.orgId },
  { usesTargetingLists: true }
);
